import { useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import {Calendar, ArrowLeft, Tag} from 'lucide-react'
import Newsletter from '../components/Newsletter'

const articles: Record<string, any> = {
  'glen-ivy-costco-guide-2025': {
    title: 'How to find Glen Ivy Coupons at Costco (2025 Guide)',
    date: 'January 15, 2025',
    category: 'Costco Deals',
    content: `
      <p class="lead">Costco is one of the best places to score discounted Glen Ivy Hot Springs tickets and gift cards. In this comprehensive guide, we'll show you exactly how to find these deals and maximize your savings.</p>

      <h2>Where to Find Glen Ivy at Costco</h2>
      <p>Glen Ivy Hot Springs gift cards appear in three main locations at Costco:</p>
      <ul>
        <li><strong>In-Warehouse Gift Card Section:</strong> Check the gift card display near the front of the store</li>
        <li><strong>Costco.com:</strong> Search "Glen Ivy" in the Gift Cards category</li>
        <li><strong>Seasonal Promotions:</strong> Watch for special displays during peak spa season (spring and fall)</li>
      </ul>

      <h2>Typical Savings at Costco</h2>
      <p>Costco typically offers Glen Ivy gift cards at 15-20% below face value. For example:</p>
      <ul>
        <li>$100 gift card for $79.99 (20% savings)</li>
        <li>$50 gift card for $42.99 (14% savings)</li>
      </ul>

      <h2>Best Times to Buy</h2>
      <p>The best times to purchase Glen Ivy gift cards at Costco are:</p>
      <ul>
        <li><strong>January-February:</strong> Post-holiday inventory clearance</li>
        <li><strong>April-May:</strong> Mother's Day promotions</li>
        <li><strong>September:</strong> Back-to-school season specials</li>
      </ul>

      <h2>Pro Tips for Maximum Savings</h2>
      <ol>
        <li><strong>Use Costco Credit Card:</strong> Earn 2% cash back on Costco purchases</li>
        <li><strong>Buy During Sales:</strong> Occasionally, Costco runs additional promotions on gift cards</li>
        <li><strong>Check Expiration Dates:</strong> Glen Ivy gift cards typically have 1-2 year expiration dates</li>
        <li><strong>Stack with Weekday Discounts:</strong> Use your Costco gift cards on weekdays for additional savings</li>
      </ol>

      <h2>Membership Requirements</h2>
      <p>You'll need a valid Costco membership to purchase Glen Ivy gift cards. If you don't have one, consider:</p>
      <ul>
        <li>Asking a friend or family member with a membership to buy on your behalf</li>
        <li>Signing up for a Costco membership (the savings on gift cards alone can pay for the membership fee)</li>
      </ul>

      <h2>Conclusion</h2>
      <p>Shopping for Glen Ivy gift cards at Costco is one of the easiest ways to save 15-20% on spa day expenses. Combined with strategic timing and credit card rewards, you can maximize your discount even further.</p>
    `
  },
  'glens-army-navy-free-shipping': {
    title: "Glen's Army Navy Store: How to score free shipping",
    date: 'January 10, 2025',
    category: 'Shopping Tips',
    content: `
      <p class="lead">Free shipping can save you significant money on outdoor gear and military surplus items. Here's your complete guide to getting free shipping at Glen's Army Navy Store.</p>

      <h2>Current Free Shipping Offers</h2>
      <p>Glen's Army Navy regularly offers these free shipping promotions:</p>
      <ul>
        <li><strong>Orders Over $50:</strong> Most common threshold for free standard shipping</li>
        <li><strong>Email Subscriber Exclusive:</strong> Subscribe for occasional free shipping codes</li>
        <li><strong>Military Discount:</strong> Veterans and active duty military get free shipping year-round</li>
      </ul>

      <h2>Best Free Shipping Strategies</h2>
      
      <h3>1. Bundle Your Orders</h3>
      <p>Instead of making multiple small purchases, wait until you have $50+ worth of items to buy all at once.</p>

      <h3>2. Join the Email List</h3>
      <p>Subscribers receive exclusive promo codes including free shipping offers 2-3 times per month.</p>

      <h3>3. Shop During Major Sales</h3>
      <p>Free shipping is often included during:</p>
      <ul>
        <li>Black Friday / Cyber Monday</li>
        <li>Memorial Day</li>
        <li>Veterans Day</li>
        <li>Independence Day</li>
      </ul>

      <h2>Active Free Shipping Codes</h2>
      <p>Current verified codes (updated January 2025):</p>
      <ul>
        <li><strong>FREESHIP50:</strong> Free shipping on orders $50+</li>
        <li><strong>MILITARY15:</strong> 15% off + free shipping for military members</li>
      </ul>

      <h2>Alternative Shipping Options</h2>
      <p>If you can't reach the free shipping threshold:</p>
      <ul>
        <li><strong>Store Pickup:</strong> Free pickup at physical locations</li>
        <li><strong>Standard Shipping:</strong> Usually $5.95 for orders under $50</li>
      </ul>

      <h2>Final Tips</h2>
      <ol>
        <li>Always check for promo codes before checkout</li>
        <li>Add items to cart and wait - you may receive an abandoned cart email with free shipping</li>
        <li>Follow Glen's Army Navy on social media for flash free shipping promotions</li>
      </ol>
    `
  },
  'hidden-promo-codes-glencode': {
    title: 'Hidden Promo Codes: The Glencode Official List',
    date: 'January 5, 2025',
    category: 'Promo Codes',
    content: `
      <p class="lead">Welcome to the ultimate collection of verified promo codes for all Glen-branded businesses. These are exclusive codes you won't find on typical coupon sites.</p>

      <h2>Glen Ivy Hot Springs</h2>
      <h3>Active Codes (Verified Jan 2025)</h3>
      <ul>
        <li><strong>WEEKDAY25:</strong> 25% off weekday admission (Mon-Fri)</li>
        <li><strong>COUPLES100:</strong> $100 off couples spa package</li>
        <li><strong>BIRTHDAY20:</strong> 20% off during your birthday month (ID required)</li>
        <li><strong>SENIOR15:</strong> 15% off for seniors 65+ (proof of age required)</li>
      </ul>

      <h2>Glen's Army Navy</h2>
      <h3>Active Codes (Verified Jan 2025)</h3>
      <ul>
        <li><strong>FREESHIP50:</strong> Free shipping on orders $50+</li>
        <li><strong>MILITARY15:</strong> 15% off for military members (verification required)</li>
        <li><strong>WINTER20:</strong> 20% off winter clearance items</li>
        <li><strong>FIRSTORDER10:</strong> 10% off your first purchase</li>
      </ul>

      <h2>Mapleglen</h2>
      <h3>Active Codes (Verified Jan 2025)</h3>
      <ul>
        <li><strong>SPRING30:</strong> 30% off spring collection</li>
        <li><strong>BUNDLE15:</strong> 15% off when you buy 3+ items</li>
        <li><strong>VIP20:</strong> 20% off for email subscribers</li>
      </ul>

      <h2>Gleneagles Golf</h2>
      <h3>Active Codes (Verified Jan 2025)</h3>
      <ul>
        <li><strong>TWILIGHT25:</strong> 25% off twilight tee times</li>
        <li><strong>GROUP20:</strong> 20% off groups of 4 or more</li>
        <li><strong>EARLYBIRD:</strong> $10 off morning tee times before 8 AM</li>
      </ul>

      <h2>How to Use These Codes</h2>
      <ol>
        <li>Add items to your cart or select your service</li>
        <li>Proceed to checkout</li>
        <li>Look for "Promo Code" or "Discount Code" field</li>
        <li>Enter the code exactly as shown (case-sensitive)</li>
        <li>Click "Apply" and verify the discount appears</li>
      </ol>

      <h2>Code Stacking Tips</h2>
      <p>Some businesses allow you to stack multiple discounts:</p>
      <ul>
        <li><strong>Glen Ivy:</strong> Birthday discount + weekday discount (in some cases)</li>
        <li><strong>Glen's Army Navy:</strong> Sale items + free shipping code</li>
        <li><strong>Mapleglen:</strong> Bundle discount + email subscriber code</li>
      </ul>

      <h2>Exclusive GlenCode Member Perks</h2>
      <p>Subscribe to our newsletter for:</p>
      <ul>
        <li>Early access to new promo codes</li>
        <li>Exclusive GlenCode-only discounts</li>
        <li>Weekly deal alerts</li>
        <li>Seasonal promotion calendars</li>
      </ul>

      <h2>Code Expiration Policy</h2>
      <p>We update this list weekly. All codes are verified before publication. If a code doesn't work:</p>
      <ul>
        <li>Check for typos (codes are case-sensitive)</li>
        <li>Verify you meet the requirements (minimum purchase, specific items, etc.)</li>
        <li>Contact us to report expired codes</li>
      </ul>

      <p><em>Last updated: January 5, 2025 | Next update: January 12, 2025</em></p>
    `
  }
}

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>()
  const article = slug ? articles[slug] : null

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | GlenCode Blog`
      const metaDescription = document.querySelector('meta[name="description"]')
      if (metaDescription) {
        metaDescription.setAttribute('content', article.title)
      }
    }
  }, [article])

  if (!article) {
    return (
      <div className="min-h-screen bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-green-600 hover:text-green-700 font-semibold">
            ← Back to Blog
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Article Header */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-semibold mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition" />
            Back to Blog
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full flex items-center gap-2">
                <Tag className="w-4 h-4" />
                {article.category}
              </span>
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Calendar className="w-4 h-4" />
                <span>{article.date}</span>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {article.title}
            </h1>
          </div>

          {/* Article Content */}
          <div
            className="prose prose-lg max-w-none
              prose-headings:font-bold prose-headings:text-gray-900
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4
              prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
              prose-ul:my-6 prose-ul:space-y-2
              prose-ol:my-6 prose-ol:space-y-2
              prose-li:text-gray-700
              prose-strong:text-gray-900 prose-strong:font-semibold
              prose-a:text-green-600 prose-a:no-underline hover:prose-a:text-green-700"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>
      </article>

      {/* Newsletter */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Newsletter />
        </div>
      </section>
    </div>
  )
}
